import Loading from "../../../components/loading";
import { formatarXml } from "../../../util/xml";
import Badge from "./badge";
import { Cliente, dataHora, moeda } from "./tabela-fiscal";
import { MODELOS, STATUS, TONS } from "../status";

const ESTILO_LINHA = {
	item: "text-sm",
	pai: "text-sm font-semibold bg-slate-50",
	complemento: "text-xs text-slate-500",
};

const PONTO = {
	verde: "bg-emerald-500",
	vermelho: "bg-red-500",
	ambar: "bg-amber-500",
	cinza: "bg-gray-400",
	neutro: "bg-customBlue-primary",
};

function Dado({ rotulo, largo, children }) {
	return (
		<div className={largo ? "col-span-2" : ""}>
			<dt className="text-xs font-normal text-gray-500">{rotulo}</dt>
			<dd className="text-sm text-gray-800 mb-0">{children || "—"}</dd>
		</div>
	);
}

function Vazio({ children }) {
	return <p className="text-sm text-gray-500 mb-0">{children}</p>;
}

function Carga({ estado, vazio, children }) {
	if (!estado || estado.carregando) {
		return (
			<div className="flex h-44">
				<Loading mensagem="Carregando..." />
			</div>
		);
	}

	if (estado.erro) {
		return (
			<div className="rounded-md border px-3 py-2 text-sm bg-red-50 text-red-700 border-red-200">{estado.erro}</div>
		);
	}

	return estado.dados ? children(estado.dados) : <Vazio>{vazio}</Vazio>;
}

function BlocoXml({ titulo, conteudo }) {
	return (
		<div className="flex flex-col gap-1">
			<span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{titulo}</span>
			<pre className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs overflow-auto whitespace-pre-wrap break-all mb-0">
				{formatarXml(conteudo)}
			</pre>
		</div>
	);
}

export function AbaResumo({ documento }) {
	const retorno = [documento.cstat, documento.motivo].filter(Boolean).join(" · ");
	const chave = documento.chave?.replace(/(\d{4})(?=\d)/g, "$1 ");

	return (
		<div className="flex flex-col gap-4">
			{retorno && (
				<div className={`rounded-md border px-3 py-2 text-sm ${TONS[STATUS[documento.status].tom]}`}>
					<div className="text-xs font-semibold uppercase tracking-wide opacity-80">Retorno da SEFAZ</div>
					{retorno}
				</div>
			)}

			<dl className="grid grid-cols-2 gap-x-4 gap-y-3 mb-0">
				<Dado rotulo="Modelo">{MODELOS[documento.modelo]}</Dado>
				<Dado rotulo="Série / Número">
					{documento.serie} / {documento.numero}
				</Dado>
				<Dado rotulo="Emissão">{dataHora(documento.data_emissao)}</Dado>
				<Dado rotulo="Valor">{moeda(documento.valor_total)}</Dado>
				<Dado rotulo="Ambiente">{documento.ambiente}</Dado>
				<Dado rotulo="Tentativas de envio">{String(documento.tentativas)}</Dado>
				<Dado rotulo="Protocolo">{documento.protocolo}</Dado>
				<Dado rotulo="Venda">{documento.id_venda}</Dado>
				<Dado rotulo="Destinatário" largo>
					<Cliente nome={documento.cliente} documento={documento.dest_cpfcnpj} />
				</Dado>
				<Dado rotulo="Chave de acesso" largo>
					{chave && <span className="font-mono text-xs break-all">{chave}</span>}
				</Dado>
			</dl>
		</div>
	);
}

export function AbaEventos({ documento }) {
	const ultimo = documento.eventos.length - 1;

	return (
		<ol className="relative ml-2 pl-0 mb-0">
			{documento.eventos.map((evento, i) => (
				<li key={i} className="relative list-none ml-5 pb-5 last:pb-0">
					{i < ultimo && <span className="absolute -left-[22px] top-2 -bottom-2 w-0.5 bg-gray-300" />}
					<span className={`absolute -left-[27px] top-1 h-3 w-3 rounded-full ring-4 ring-white ${PONTO[evento.tom]}`} />
					<div className="text-sm font-medium text-gray-800">{evento.titulo}</div>
					<div className="text-xs text-gray-500">{dataHora(evento.data)}</div>
					{evento.detalhe && <div className="text-xs text-gray-600 mt-0.5">{evento.detalhe}</div>}
				</li>
			))}
		</ol>
	);
}

export function AbaXml({ estado }) {
	return (
		<Carga estado={estado}>
			{({ xml, xml_evento }) =>
				xml ? (
					<div className="flex flex-col gap-4">
						{xml_evento && <BlocoXml titulo="Evento" conteudo={xml_evento} />}
						<BlocoXml titulo="Documento" conteudo={xml} />
					</div>
				) : (
					<Vazio>O XML ainda não foi gerado para este documento.</Vazio>
				)
			}
		</Carga>
	);
}

export function AbaVenda({ estado }) {
	return (
		<Carga estado={estado} vazio="Nenhuma venda relacionada a este documento.">
			{(venda) => <DadosVenda venda={venda} />}
		</Carga>
	);
}

function DadosVenda({ venda }) {
	return (
		<div className="flex flex-col gap-4">
			<dl className="grid grid-cols-2 gap-x-4 gap-y-3 mb-0">
				<Dado rotulo="Venda">{venda.id}</Dado>
				<Dado rotulo="Situação">
					<Badge rotulo={venda.situacao} tom={venda.situacao === "Ativa" ? "verde" : "cinza"} />
				</Dado>
				<Dado rotulo="Data">{dataHora(venda.data)}</Dado>
				<Dado rotulo="Pagamento">{venda.pagamento}</Dado>
				<Dado rotulo="Cliente" largo>
					<Cliente nome={venda.cliente} documento={venda.dest_cpfcnpj} />
				</Dado>
			</dl>

			<table className="table table-portal mb-0 w-full border">
				<thead className="color-table">
					<tr>
						<th className="text-left font-semibold py-2">
							<span className="text-color-table">Produto</span>
						</th>
						<th className="text-center font-semibold py-2 w-16">
							<span className="text-color-table">Qtd</span>
						</th>
						<th className="text-right font-semibold py-2 w-28">
							<span className="text-color-table">Total</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{venda.itens.map((item, i) => (
						<tr key={i} className={ESTILO_LINHA[item.tipo]}>
							<td className={item.tipo === "complemento" ? "pl-6" : ""}>{item.descricao}</td>
							<td className="text-center">{item.quantidade}</td>
							<td className="text-right">{item.total === null ? "incluso" : moeda(item.total)}</td>
						</tr>
					))}
					<tr>
						<td colSpan={2} className="text-right text-sm font-semibold">
							Total da venda
						</td>
						<td className="text-right text-sm font-semibold">{moeda(venda.total)}</td>
					</tr>
				</tbody>
			</table>
		</div>
	);
}
